import {
  Component,
  input,
  output,
  signal,
  inject,
  OnInit,
  computed
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import {
  Persona,
  PersonaFiltro,
  createEmptyFiltro,
  getNombreCompleto,
  TIPO_NIT,
  TIPO_CEDULA
} from 'src/app/domain/model/persona/persona';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';
import {
  onlyLettersKeypress,
  identificacionKeypress,
  sanitizeLettersOnly,
  sanitizeIdentificacion
} from 'src/app/common/input-utils';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Persona seleccionada desde el buscador, ya con los campos que "Crear
 * Empleado" necesita precargar de solo lectura (legacy: `siBuscarPersona`,
 * solo existe en `CrearEmpleadoViewImpl`, no en Modificar — punto 1/5 de
 * "Diferencias Crear vs Modificar" de HU1259115.md).
 */
export interface PersonaSeleccionada {
  cdpersona: number;
  cdtipoIdentificacion: string;
  nmidentificacion: string;
  nombreCompleto: string;
  dsemail?: string;
}

/**
 * Diálogo de búsqueda y selección de Persona, reutilizado por "Crear
 * Empleado" (HU1259115) para el widget legacy `siBuscarPersona`. No existe
 * hoy en el proyecto un componente de selección de persona reutilizable — el
 * único módulo de Personas existente (`PersonasListComponent`) es un CRUD
 * completo sin modo "seleccionar", así que este diálogo es nuevo pero
 * calca la estructura ya validada de `BusquedaClienteDialogComponent`
 * (mismos filtros/paginación que `PersonasListComponent`, presentados como
 * diálogo de selección de una sola fila en vez de listado con edición).
 *
 * <p>Legacy: `BuscadorPersonaViewImpl`/`SearchPersonActivity` — mismos
 * filtros de tipo/número de identificación, nombre1/nombre2/apellido1/
 * apellido2 (persona natural) o nombre comercial (NIT) usados en
 * `AdminPersonasView`/`PersonasListComponent`.</p>
 */
@Component({
  selector: 'app-buscador-persona-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Buscar Persona"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '52rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <div class="border dark:border-gray-600 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800">
        <div class="font-medium text-gray-700 dark:text-gray-300 mb-3 text-sm">Filtros de Búsqueda</div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Tipo Identificación</label>
            <p-dropdown [(ngModel)]="filtro.tipoId" [options]="tiposIdentificacion()"
              optionLabel="descripcion" optionValue="codigo" placeholder="Tipo..."
              [showClear]="true" [filter]="true" filterBy="descripcion" appendTo="body"
              (onChange)="onTipoIdChange()" [style]="{ width: '100%' }">
            </p-dropdown>
          </div>
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Número Identificación</label>
            <input pInputText [(ngModel)]="filtro.numId" class="w-full" maxlength="20"
              (keypress)="onIdentificacionKeypress($event)" />
          </div>
        </div>

        @if (esNit()) {
          <div class="grid grid-cols-1 gap-3">
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Nombre Comercial</label>
              <!-- Sanitización en (input) — no en (change)/blur como el legado
                   (Utilidades.getTextBoxChangeHandler(false)) — es la práctica
                   exigida por seguridad-girop §3 para todo campo de texto libre
                   de este formulario, más estricta que el legado, no una regresión. -->
              <input pInputText [(ngModel)]="filtro.nombreComercial" class="w-full" maxlength="100"
                (input)="filtro.nombreComercial = sanitizeName(filtro.nombreComercial)" />
            </div>
          </div>
        } @else {
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Primer Nombre</label>
              <input pInputText [(ngModel)]="filtro.nombre1" class="w-full" maxlength="30"
                (input)="filtro.nombre1 = sanitizeName(filtro.nombre1)" />
            </div>
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Segundo Nombre</label>
              <input pInputText [(ngModel)]="filtro.nombre2" class="w-full" maxlength="30"
                (input)="filtro.nombre2 = sanitizeName(filtro.nombre2)" />
            </div>
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Primer Apellido</label>
              <input pInputText [(ngModel)]="filtro.apellido1" class="w-full" maxlength="30"
                (input)="filtro.apellido1 = sanitizeName(filtro.apellido1)" />
            </div>
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Segundo Apellido</label>
              <input pInputText [(ngModel)]="filtro.apellido2" class="w-full" maxlength="30"
                (input)="filtro.apellido2 = sanitizeName(filtro.apellido2)" />
            </div>
          </div>
        }

        <div class="mt-3">
          <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Activo</label>
          <div class="flex flex-wrap gap-4 pt-1">
            <div class="flex items-center">
              <p-radioButton name="buscadorPersonaActivo" value="S" [(ngModel)]="filtro.activo" inputId="buscadorPersonaActivoSi"></p-radioButton>
              <label for="buscadorPersonaActivoSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="buscadorPersonaActivo" value="N" [(ngModel)]="filtro.activo" inputId="buscadorPersonaActivoNo"></p-radioButton>
              <label for="buscadorPersonaActivoNo" class="ml-2 text-sm">No</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="buscadorPersonaActivo" value="" [(ngModel)]="filtro.activo" inputId="buscadorPersonaActivoTodos"></p-radioButton>
              <label for="buscadorPersonaActivoTodos" class="ml-2 text-sm">Todos</label>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 sm:gap-3 mt-4 justify-end sm:justify-start w-full">
          <p-button label="Limpiar" icon="pi pi-eraser" severity="secondary" [outlined]="true" size="small"
            styleClass="min-h-[44px]" (onClick)="limpiarFiltros()" />
          <p-button label="Buscar" icon="pi pi-search" size="small" [loading]="loading()"
            styleClass="min-h-[44px]" (onClick)="buscar()" />
        </div>
      </div>

      @if (mensaje()) {
        <div class="p-3 mb-3 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
          <i class="pi pi-exclamation-triangle mr-2"></i>{{ mensaje() }}
        </div>
      }

      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
        </div>
      }

      <div class="border dark:border-gray-600 rounded">
        <div class="font-medium text-gray-700 dark:text-gray-300 p-3 border-b dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center">
          <span>Listado de Personas</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500">Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de {{ totalRegistros() }}</span>
          }
        </div>

        @if (!loading() && items().length > 0) {
          <div class="md:hidden space-y-3 p-3">
            @for (item of items(); track item.cdpersona) {
              <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
                <div class="mb-3 min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">Nombre</div>
                  <div class="font-semibold text-gray-800 dark:text-gray-100 truncate">{{ textoTabla(nombreCompleto(item)) }}</div>
                </div>
                <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                  <div><span class="text-gray-500 dark:text-gray-400">Tipo Identificación:</span> {{ textoTabla(descripcionTipoId(item)) }}</div>
                  <div><span class="text-gray-500 dark:text-gray-400">Número Identificación:</span> {{ textoTabla(item.nmidentificacion) }}</div>
                </div>
                <p-button label="Seleccionar" icon="pi pi-check" size="small" styleClass="w-full min-h-[44px]"
                  (onClick)="seleccionarItem(item)" />
              </div>
            }
          </div>

          <div class="hidden md:block">
            <p-table [value]="items()" [scrollable]="true" [responsiveLayout]="'scroll'" scrollHeight="250px"
              [resizableColumns]="true" columnResizeMode="expand"
              [tableStyle]="{ 'min-width': 'max-content' }" styleClass="p-datatable-sm p-datatable-gridlines p-datatable-striped tabla-buscador-persona">
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn><span class="grid-header-text" title="Tipo Identificación">Tipo Identificación</span></th>
                  <th pResizableColumn><span class="grid-header-text" title="Número Identificación">Número Identificación</span></th>
                  <th pResizableColumn><span class="grid-header-text" title="Nombre">Nombre</span></th>
                  <th pResizableColumn><span class="grid-header-text" title="Seleccionar">Seleccionar</span></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr>
                  <td><span class="grid-cell-text">{{ textoTabla(descripcionTipoId(item)) }}</span></td>
                  <td><span class="grid-cell-text">{{ textoTabla(item.nmidentificacion) }}</span></td>
                  <td><span class="grid-cell-text">{{ textoTabla(nombreCompleto(item)) }}</span></td>
                  <td>
                    <button pButton label="Seleccionar" class="p-button-sm p-button-text"
                      (click)="seleccionarItem(item)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <app-busqueda-paginador
            [currentPage]="paginaActual()"
            [totalPages]="totalPaginas()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize"
            (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="loadPreviousPage()"
            (next)="loadNextPage()"
            (last)="irUltimaPagina()">
          </app-busqueda-paginador>
        }

        @if (!loading() && items().length === 0) {
          <div class="text-center text-gray-500 p-4">No hay datos</div>
        }
      </div>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button label="Cerrar" icon="pi pi-times" [outlined]="true" styleClass="min-h-[44px]" (onClick)="onCancelar()"></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BuscadorPersonaDialogComponent implements OnInit {
  private readonly personaRepository = inject(PersonaRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);

  /** Permite excluir del listado personas que ya tienen empleado asociado — no aplica hoy (no verificado contra legado), se deja abierto para uso futuro. */
  readonly titulo = input<string>('Buscar Persona');

  onSelect = output<PersonaSeleccionada>();
  onClose = output<void>();

  visible = true;
  filtro: PersonaFiltro = createEmptyFiltro();

  items = signal<Persona[]>([]);
  loading = signal(false);
  mensaje = signal('');
  tiposIdentificacion = signal<CatalogoOption[]>([]);

  totalRegistros = signal(0);
  paginaActual = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  totalPaginas = computed(() => {
    const total = this.totalRegistros();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  hasNext = signal(false);
  hasPrevious = signal(false);

  // Cursores opaques devueltos por el backend (keyset pagination) — nunca se
  // reconstruyen en el cliente a partir de un campo del registro (ver
  // personas-list.component.ts: mismo patrón). El orden ya no es por
  // CDPERSONA, así que un cursor armado con `String(item.cdpersona)` dejó de
  // ser válido cuando el backend pasó a ordenar fiel al legado (tipo de
  // identificación + nombre comercial, desempate por CDPERSONA).
  nextCursor = signal<string | null>(null);
  previousCursor = signal<string | null>(null);

  registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.paginaActual() - 1) * this.pageSize() + 1;
  });

  registroFin = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    const fin = this.registroInicio() + this.items().length - 1;
    return Math.min(fin, this.totalRegistros());
  });

  sanitizeName = sanitizeLettersOnly;
  onIdentificacionKeypress = identificacionKeypress;
  nombreCompleto = getNombreCompleto;

  esNit(): boolean {
    return this.filtro.tipoId === TIPO_NIT;
  }

  ngOnInit(): void {
    this.cargarTiposIdentificacion();
  }

  private async cargarTiposIdentificacion(): Promise<void> {
    try {
      this.tiposIdentificacion.set(await this.catalogoRepository.getTiposIdentificacion());
    } catch (error) {
      console.error('Error cargando tipos de identificación:', error);
    } finally {
      // Legado: `ConsultarPersonasViewImpl.reset()` — el combo Tipo
      // Identificación siempre arranca en Cédula de Ciudadanía
      // (`Utilidades.getDefaultValueIndex(lbTipoIdPersona,
      // Constants.LIST_VALUE_TYPE_ID_CC)`), tanto al abrir la pantalla como al
      // limpiar filtros. Se aplica aquí (después de poblar el catálogo, no
      // antes) porque el p-dropdown solo puede mostrar seleccionado un valor
      // que ya exista entre sus `options`. Es un valor inicial, no una
      // restricción: el usuario puede cambiarlo libremente después.
      this.filtro.tipoId = TIPO_CEDULA;
    }
  }

  onTipoIdChange(): void {
    this.filtro.nombreComercial = '';
    this.filtro.nombre1 = '';
    this.filtro.nombre2 = '';
    this.filtro.apellido1 = '';
    this.filtro.apellido2 = '';
  }

  async buscar(): Promise<void> {
    this.mensaje.set('');
    this.paginaActual.set(1);
    await this.buscarPagina(undefined, 'NEXT');
  }

  private async buscarPagina(cursor: string | undefined, direction: 'NEXT' | 'PREVIOUS'): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');
    try {
      const size = this.pageSize();
      const response = await this.personaRepository.getPageWithFiltersAndDirection(cursor, size, this.filtro, direction);
      this.items.set(response.content);
      this.nextCursor.set(response.nextCursor ?? null);
      this.previousCursor.set(response.previousCursor ?? null);
      this.hasNext.set(!!response.nextCursor);
      this.hasPrevious.set(!!response.previousCursor);
      if (cursor === undefined) {
        this.totalRegistros.set(await this.personaRepository.countWithFilters(this.filtro));
      }
      if (response.content.length === 0) {
        this.mensaje.set('No se recuperó información con los parámetros ingresados.');
      }
    } catch (error) {
      console.error('Error en búsqueda de personas:', error);
      this.mensaje.set('Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.');
      this.items.set([]);
      this.totalRegistros.set(0);
      this.hasNext.set(false);
      this.hasPrevious.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  limpiarFiltros(): void {
    this.filtro = createEmptyFiltro();
    // Mismo default de Cédula de Ciudadanía que en la carga inicial (ver
    // cargarTiposIdentificacion) — el catálogo ya está cargado en este punto
    // (el diálogo ya está abierto), así que se aplica de inmediato.
    this.filtro.tipoId = TIPO_CEDULA;
    this.items.set([]);
    this.mensaje.set('');
    this.totalRegistros.set(0);
    this.paginaActual.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursor.set(null);
    this.previousCursor.set(null);
  }

  async onPageSizeChange(): Promise<void> {
    this.pageSize.set(this.selectedPageSize);
    this.paginaActual.set(1);
    await this.buscarPagina(undefined, 'NEXT');
  }

  async irPrimeraPagina(): Promise<void> {
    this.paginaActual.set(1);
    await this.buscarPagina(undefined, 'NEXT');
  }

  async loadNextPage(): Promise<void> {
    if (!this.hasNext()) return;
    const cursor = this.nextCursor();
    if (!cursor) return;
    this.paginaActual.update((p) => p + 1);
    await this.buscarPagina(cursor, 'NEXT');
  }

  async loadPreviousPage(): Promise<void> {
    if (!this.hasPrevious() || this.paginaActual() <= 1) return;
    const cursor = this.previousCursor();
    this.paginaActual.update((p) => Math.max(1, p - 1));
    await this.buscarPagina(cursor ?? undefined, 'PREVIOUS');
  }

  async irUltimaPagina(): Promise<void> {
    // No hay endpoint de "última página" para Persona con filtros estructurados
    // desde este diálogo — se navega manualmente con "siguiente" (mismo límite
    // que ya tenía el buscador genérico de clientes para pólizas, no es una
    // regresión nueva de este componente).
    if (!this.hasNext()) return;
    while (this.hasNext()) {
      await this.loadNextPage();
    }
  }

  seleccionarItem(item: Persona): void {
    if (item.cdpersona == null) {
      return;
    }
    const resultado: PersonaSeleccionada = {
      cdpersona: item.cdpersona,
      cdtipoIdentificacion: item.cdtipo_identificacion ?? '',
      nmidentificacion: item.nmidentificacion ?? '',
      nombreCompleto: getNombreCompleto(item),
      dsemail: item.dsemail
    };
    this.onSelect.emit(resultado);
    this.visible = false;
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /**
   * Descripción completa del tipo de identificación para mostrar en la
   * grilla (ej. "CÉDULA DE CIUDADANÍA" en vez de "C"). El backend ya expone
   * `dstipoIdentificacion` (JOIN TPSC_TIPOS_IDENTIFICACION, ver
   * PersonaResponse/PersonaWebMapper); si por algún motivo llega vacío
   * (dato legado sin catálogo asociado, o respuesta más antigua sin el
   * campo), se hace fallback al código crudo en vez de dejar la celda vacía.
   */
  descripcionTipoId(item: Persona): string {
    return item.dstipoIdentificacion || item.cdtipo_identificacion || '';
  }
}
